import {useEffect} from 'react'

import {
  isPendingAutoSubmitCurrent,
  useInjectedReferencesActions,
  useInjectedReferencesData,
} from '@github-ui/copilot-shared-ui/contexts/InjectedReferencesContext'

/**
 * Dispatches a prompt that arrived on an `open-copilot-chat` event exactly once,
 * through the composer's normal submit path so implicit context still resolves.
 */
export function usePendingAutoSubmit({
  readOnly,
  ready,
  submitPrompt,
}: {
  readOnly: boolean
  ready: boolean
  submitPrompt: (prompt: string) => Promise<boolean>
}) {
  const {pendingAutoSubmit, submitInFlight} = useInjectedReferencesData()
  const {startPendingAutoSubmit, releasePendingAutoSubmit, completePendingAutoSubmit} = useInjectedReferencesActions()

  useEffect(() => {
    if (pendingAutoSubmit === null || submitInFlight) return

    const {id, prompt} = pendingAutoSubmit
    if (pendingAutoSubmit.blocked) {
      completePendingAutoSubmit(id)
      return
    }
    // A passive session hides the composer entirely, so consume the prompt rather
    // than dispatching into a session the user cannot see or reply in.
    if (readOnly) {
      completePendingAutoSubmit(id)
      return
    }
    if (!isPendingAutoSubmitCurrent(pendingAutoSubmit)) {
      completePendingAutoSubmit(id)
      return
    }

    // The retained intent must outlive a composer mounted before its runtime.
    // Do not claim/release it repeatedly while provisioning is unavailable.
    if (!ready || !startPendingAutoSubmit(id)) return
    void (async () => {
      try {
        const didSubmit = await submitPrompt(prompt)
        if (didSubmit) {
          completePendingAutoSubmit(id)
        } else {
          // Dispatch never started, so let a later composer retry.
          releasePendingAutoSubmit(id)
        }
      } catch {
        completePendingAutoSubmit(id)
      }
    })()
  }, [
    completePendingAutoSubmit,
    pendingAutoSubmit,
    submitInFlight,
    readOnly,
    ready,
    releasePendingAutoSubmit,
    startPendingAutoSubmit,
    submitPrompt,
  ])
}
